import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => `UGX ${Number(v || 0).toLocaleString()}`;
const YEARS = ['2024', '2025', '2026', '2027'];

// Yearly per-sponsor statements: printable PDFs of exactly how each child's money was used.
export const SponsorStatementsTab = () => {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState({ items: [] });

  const load = useCallback(() => {
    axios.get(`${API}/api/sponsorship/statements?year=${year}`).then((r) => setData(r.data)).catch(() => {});
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const download = async (sponsor) => {
    const p = new URLSearchParams({ year });
    if (sponsor) p.append('sponsor', sponsor);
    try {
      const r = await axios.get(`${API}/api/sponsorship/statements/pdf?${p}`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = `sponsor_statement_${year}${sponsor ? `_${sponsor.replace(/\s+/g, '_')}` : '_all'}.pdf`;
      a.click();
      toast.success('Statement PDF downloaded — ready to print or send');
    } catch { toast.error('Statement generation failed'); }
  };

  const th = 'px-3 py-2 text-left text-[10px] font-semibold uppercase bg-muted border-b';
  const td = 'px-3 py-2 border-b text-xs';

  return (
    <div className="space-y-3" data-testid="sponsor-statements-tab">
      <div className="flex items-center gap-3">
        <Mail className="h-4 w-4 text-primary" />
        <div className="flex-1">
          <h3 className="font-heading font-semibold">Annual Sponsor Statements</h3>
          <p className="text-xs text-muted-foreground">One printable PDF per sponsor, showing every donation received and every shilling spent on their child during the year — drawn live from the Finance ledger.</p>
        </div>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-24" data-testid="statements-year"><SelectValue /></SelectTrigger>
          <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" onClick={() => download(null)} data-testid="statements-all-btn"><FileText className="h-3.5 w-3.5 mr-1" /> All Statements (PDF)</Button>
      </div>
      <Card><CardContent className="p-0">
        <table className="w-full">
          <thead><tr>{['Sponsor', 'Children', `Received ${year}`, `Spent ${year}`, 'Closing Balance', ''].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
          <tbody>
            {data.items.map((s) => (
              <tr key={s.sponsor} className="hover:bg-muted/40" data-testid={`statement-row-${s.sponsor.replace(/\s+/g, '-')}`}>
                <td className={`${td} font-medium`}>{s.sponsor}</td>
                <td className={td}>{s.children.join(', ')}</td>
                <td className={`${td} tabular-nums text-green-700`}>{fmt(s.received)}</td>
                <td className={`${td} tabular-nums text-red-600`}>{fmt(s.spent)}</td>
                <td className={`${td} tabular-nums font-semibold`}>{fmt(s.closing)}</td>
                <td className={`${td} text-right`}>
                  <Button variant="outline" size="sm" className="h-7" onClick={() => download(s.sponsor)} data-testid={`statement-pdf-${s.sponsor.replace(/\s+/g, '-')}`}>
                    <FileText className="h-3.5 w-3.5 mr-1" /> PDF
                  </Button>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground text-sm">No sponsors found</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
};

export default SponsorStatementsTab;
